// src/pages/affiliate/OffersPage.tsx
// Offers catalog — what a partner can promote. Browse, inspect payout terms
// and targeting, then start a campaign for the offer that fits.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useAffiliateOffer, useI18n } from '@react/hooks';
import { Button, Input, Alert } from '@react/components';
import { authStore } from '@core/stores';
import type { Offer } from '@core/types/affiliate';
import { OfferDetailRail } from '@/features/affiliate/OfferDetailRail';
import { CampaignFormDialog } from '@/features/affiliate/CampaignFormDialog';
import { OfferStatusBadge, offerPayoutTypeLabel } from '@/features/affiliate/badges';
import { errMessage, formatMoney, getStoredAffiliateId, listParams } from '@/features/affiliate/format';

export const OffersPage = observer(() => {
  const { t, locale } = useI18n();
  const store = useAffiliateOffer();
  const profileId = authStore.activeProfileId;
  const myAffiliateId = getStoredAffiliateId(profileId);

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [campaignDialog, setCampaignDialog] = useState<Offer | null>(null);
  const [createdMsg, setCreatedMsg] = useState(false);

  const load = () => {
    setError(null);
    store
      .ListOffers(listParams({ limit: 100 }, { status: 'OFFER_STATUS_ACTIVE', q: search || undefined }))
      .catch((e: unknown) => setError(errMessage(e, t('affiliate.offers.loadError'))));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('affiliate.offers.title')}</h1>
          <Link to="/affiliate" className="ml-auto text-sm text-primary hover:underline">
            {t('affiliate.backToDashboard')}
          </Link>
        </div>

        <div className="flex gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder={t('affiliate.offers.searchPlaceholder')}
            className="max-w-sm"
          />
          <Button size="sm" variant="secondary" onClick={load}>{t('common.search')}</Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}
        {createdMsg && <Alert variant="success" dismissible onDismiss={() => setCreatedMsg(false)}>{t('affiliate.offers.campaignCreated')}</Alert>}

        {store.isLoading && store.items.length === 0 && (
          <div className="space-y-2" aria-busy="true">
            <div className="h-20 animate-pulse rounded-md bg-muted" />
            <div className="h-20 animate-pulse rounded-md bg-muted" />
          </div>
        )}

        {!store.isLoading && store.items.length === 0 && !error && (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t('affiliate.offers.empty')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('affiliate.offers.subtitle')}</p>
          </div>
        )}

        <div className="space-y-2">
          {store.items.map((offer) => (
            <button
              key={offer.entity?.id}
              onClick={() => setSelectedId(offer.entity?.id ?? null)}
              className={`w-full rounded-md border p-3 text-left hover:bg-muted ${selectedId === offer.entity?.id ? 'border-primary' : 'border-border'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{offer.name}</span>
                <OfferStatusBadge status={offer.status} />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span>{offerPayoutTypeLabel(t, offer.payout_type)}</span>
                <span>{offer.payout_percent ? `${offer.payout_percent}%` : formatMoney(offer.payout_amount, locale)}</span>
                {offer.requires_approval && <span>{t('affiliate.offers.requiresApproval')}</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {selectedId && (
        <OfferDetailRail
          offerId={selectedId}
          onClose={() => setSelectedId(null)}
          onCreateCampaign={(offer) => setCampaignDialog(offer)}
        />
      )}

      <CampaignFormDialog
        open={!!campaignDialog}
        onClose={() => setCampaignDialog(null)}
        offerId={campaignDialog?.entity?.id}
        affiliateId={myAffiliateId ?? ''}
        onSaved={() => setCreatedMsg(true)}
      />
    </div>
  );
});
